import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { useBrowseDriveFolder } from '@/hooks';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { Folder } from 'lucide-react';

export type DriveFolderCrumb = { id: string | null; name: string };

export const DRIVE_FOLDER_ROOT_CRUMB: DriveFolderCrumb = { id: null, name: '/' };

export function driveFolderCrumbsPath(crumbs: DriveFolderCrumb[]): string {
  return crumbs.map((crumb) => crumb.name).join('/').replace(/^\/\/?/, '/') || '/';
}

export function DriveFolderPicker({
  driveId,
  crumbs,
  onCrumbsChange,
}: {
  driveId: number;
  crumbs: DriveFolderCrumb[];
  onCrumbsChange: (next: DriveFolderCrumb[]) => void;
}) {
  const current = crumbs[crumbs.length - 1];
  const browse = useBrowseDriveFolder(driveId, current.id, true);
  const folders = browse.data?.folders ?? [];

  return (
    <div className="space-y-2">
      <Breadcrumb>
        <BreadcrumbList>
          {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            return (
              <BreadcrumbItem key={`${crumb.id ?? 'root'}-${index}`}>
                {isLast ? (
                  <BreadcrumbPage>{crumb.name}</BreadcrumbPage>
                ) : (
                  <>
                    <BreadcrumbLink asChild>
                      <button type="button" onClick={() => onCrumbsChange(crumbs.slice(0, index + 1))}>
                        {crumb.name}
                      </button>
                    </BreadcrumbLink>
                    <BreadcrumbSeparator />
                  </>
                )}
              </BreadcrumbItem>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
      <div className="max-h-56 overflow-auto rounded-md border">
        {browse.isLoading ? <p className="p-3 text-sm text-muted-foreground">Loading folders...</p> : null}
        {browse.isError ? (
          <p className="p-3 text-sm text-destructive">
            {getAxiosErrorDisplayMessage(browse.error, 'Failed to list folders.')}
          </p>
        ) : null}
        {!browse.isLoading && !browse.isError && folders.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">No sub-folders.</p>
        ) : null}
        {folders.map((folder) => (
          <button
            key={folder.providerItemId}
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
            onClick={() => onCrumbsChange([...crumbs, { id: folder.providerItemId, name: folder.name }])}
          >
            <Folder className="h-4 w-4 text-muted-foreground" />
            {folder.name}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Selected: {driveFolderCrumbsPath(crumbs)}
      </p>
    </div>
  );
}
